import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  computed,
  effect,
  signal,
} from '@angular/core';
import { CurrencyPipe, DecimalPipe, PercentPipe } from '@angular/common';
import { firstValueFrom, Observable, tap } from 'rxjs';
import { ValuationTabComponent } from '../valuation-tab.component';
import type { CanComponentDeactivate } from '../../../routing/can-deactivate.guard';
import { TaxRate } from '../../../models/tax_rate.models';
import type {
  ExpenseMethod,
  IARow,
  IncomeApproachInput,
  IncomeApproachResponse,
  UnitGroup,
} from '../../../models/valuation.models';

type MetricKind = 'currency' | 'percent';

/**
 * In-memory editing shape — carries the full unit_groups array (for
 * read-only display of label/bedrooms/bathrooms/is_manager/code and for
 * editing monthly_rent by index) instead of IncomeApproachInput's
 * code-keyed unit_rents, which is only projected out at save time.
 */
interface EditableIncomeApproach {
  unit_groups: UnitGroup[];
  vacancy_rate: number | null;
  other_income: number;
  direct_assessments: number;
  direct_assessments_expense: number;
  expense_method: ExpenseMethod | null;
  expense_per_unit: number | null;
  expense_pct: number | null;
  cap_rate: number | null;
  remarks: string | null;
}

const METRIC_TOOLTIPS: Record<string, string> = {
  SGAI: 'Monthly Rent × Units × 12',
  'V&C Losses': 'SGAI × Vacancy Rate',
  EGAI: 'SGAI + V&C Losses',
  Expenses: 'Expense per Unit × Units, or EGAI × Expense %',
  NOI: 'EGAI + Expenses',
  'Overall Rate': 'Cap Rate + Tax Rate',
  Value: 'NOI ÷ Overall Rate',
};

interface IAColumn {
  key: string;
  label: string;
  isTotal: boolean;
  /** Index into unit_groups for editable columns; null for Other Income/Direct Assessments/TOTAL. */
  groupIndex: number | null;
}

interface IAMetricRow {
  label: string;
  kind: MetricKind;
  cells: (number | null)[];
}

interface IATable {
  columns: IAColumn[];
  unitsCells: (number | null)[];
  metricRows: IAMetricRow[];
}

@Component({
  selector: 'app-income-approach-tab',
  templateUrl: './income-approach-tab.component.html',
  styleUrl: './income-approach-tab.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [CurrencyPipe, DecimalPipe, PercentPipe],
})
export class IncomeApproachTabComponent
  extends ValuationTabComponent<IncomeApproachResponse>
  implements CanComponentDeactivate
{
  protected readonly tabTitle = 'Income Approach';

  readonly editedDraft = signal<EditableIncomeApproach | null>(null);
  readonly saving = signal(false);

  /** Minimum-completeness gate for the wizard (docs/valuation-feedback-2026-07-09.md
   * task 5) -- reuses the same `warnings` signal that already drives auto-edit-mode
   * above, rather than a separately guessed completeness rule. */
  readonly tabValidComputed = computed(() => {
    const d = this.data();
    return d !== null && d.income_approach.warnings.length === 0;
  });

  readonly table = computed<IATable | null>(() => {
    const draft = this.data();
    if (!draft) return null;
    return buildTable(draft);
  });

  readonly currentExpenseMethod = computed<ExpenseMethod | null>(
    () => this.currentValues()?.expense_method ?? null,
  );

  readonly overallRateFraction = computed<number | null>(() => {
    const v = this.currentValues();
    const taxRate = this.data()?.tax_rate ?? null;
    if (!v || v.cap_rate === null || taxRate === null) return null;
    return v.cap_rate + taxRate;
  });

  constructor() {
    super();

    effect(() => {
      this.valid.set(this.tabValidComputed());
    });
  }

  protected override loadData(): Observable<IncomeApproachResponse> {
    return this.valuationService.getIncomeApproach(this.sessionId()).pipe(
      tap((data) => this.applyEditModeForCurrentDraft(data)),
    );
  }

  startEdit(): void {
    const current = this.data();
    if (!current) return;
    this.editedDraft.set(toEditable(current));
    this.editing.set(true);
  }

  async save(): Promise<void> {
    const editable = this.editedDraft();
    if (!editable || this.saving()) return;
    this.saving.set(true);
    this.sessionPage.loadError.set(null);
    try {
      const response = await firstValueFrom(
        this.valuationService.saveIncomeApproach(
          this.sessionId(),
          toIncomeApproachInput(editable),
        ),
      );
      this.setData(response);
      this.applyEditModeForCurrentDraft();
    } catch {
      this.sessionPage.loadError.set('Failed to save income approach data.');
    } finally {
      this.saving.set(false);
    }
  }

  cancel(): void {
    this.editedDraft.set(null);
    this.applyEditModeForCurrentDraft();
  }

  canDeactivate(): boolean {
    if (!this.editing()) return true;
    return window.confirm('You have unsaved changes on this tab. Leave without saving?');
  }

  private applyEditModeForCurrentDraft(
    current: IncomeApproachResponse | null = this.data(),
  ): void {
    const needsEdit = (current?.income_approach.warnings.length ?? 0) > 0;
    this.editing.set(needsEdit);
    this.editedDraft.set(needsEdit && current ? toEditable(current) : null);
  }

  private currentValues(): EditableIncomeApproach | null {
    if (this.editing()) return this.editedDraft();
    const current = this.data();
    return current ? toEditable(current) : null;
  }

  monthlyRentDisplay(groupIndex: number): string {
    const value = this.currentValues()?.unit_groups[groupIndex]?.monthly_rent ?? null;
    return toNumberDisplay(value);
  }

  monthlyRentValue(groupIndex: number): number | null {
    return this.data()?.unit_groups[groupIndex]?.monthly_rent ?? null;
  }

  updateGroupRent(groupIndex: number, raw: string): void {
    const value = fromNumberDisplay(raw);
    this.editedDraft.update((d) => {
      if (!d) return d;
      const unit_groups = d.unit_groups.map((g, i) =>
        i === groupIndex ? { ...g, monthly_rent: value } : g,
      );
      return { ...d, unit_groups };
    });
  }

  otherIncomeDisplay(): string {
    return toNumberDisplay(this.currentValues()?.other_income ?? null);
  }

  updateOtherIncome(raw: string): void {
    const value = fromNumberDisplayOrZero(raw);
    this.editedDraft.update((d) => (d ? { ...d, other_income: value } : d));
  }

  directAssessmentsDisplay(): string {
    return toNumberDisplay(this.currentValues()?.direct_assessments ?? null);
  }

  updateDirectAssessments(raw: string): void {
    const value = fromNumberDisplayOrZero(raw);
    this.editedDraft.update((d) => (d ? { ...d, direct_assessments: value } : d));
  }

  directAssessmentsExpenseDisplay(): string {
    return toNumberDisplay(this.currentValues()?.direct_assessments_expense ?? null);
  }

  updateDirectAssessmentsExpense(raw: string): void {
    const value = fromNumberDisplayOrZero(raw);
    this.editedDraft.update((d) => (d ? { ...d, direct_assessments_expense: value } : d));
  }

  vacancyRateDisplay(): string {
    return toPercentDisplay(this.currentValues()?.vacancy_rate ?? null);
  }

  updateVacancyRate(raw: string): void {
    const value = fromPercentDisplay(raw);
    this.editedDraft.update((d) => (d ? { ...d, vacancy_rate: value } : d));
  }

  capRateDisplay(): string {
    return toPercentDisplay(this.currentValues()?.cap_rate ?? null);
  }

  updateCapRate(raw: string): void {
    const value = fromPercentDisplay(raw);
    this.editedDraft.update((d) => (d ? { ...d, cap_rate: value } : d));
  }

  taxRateDisplay(): string {
    const rate = new TaxRate(this.data()?.tax_rate ?? null);
    return rate.stringValue ? `${rate.stringValue}%` : '—';
  }

  expensePerUnitDisplay(): string {
    return toNumberDisplay(this.currentValues()?.expense_per_unit ?? null);
  }

  updateExpensePerUnit(raw: string): void {
    const value = fromNumberDisplay(raw);
    this.editedDraft.update((d) => (d ? { ...d, expense_per_unit: value } : d));
  }

  expensePctDisplay(): string {
    return toPercentDisplay(this.currentValues()?.expense_pct ?? null);
  }

  updateExpensePct(raw: string): void {
    const value = fromPercentDisplay(raw);
    this.editedDraft.update((d) => (d ? { ...d, expense_pct: value } : d));
  }

  updateExpenseMethod(method: string): void {
    const value: ExpenseMethod | null =
      method === 'per_unit' || method === 'pct_egai' ? method : null;
    this.editedDraft.update((d) => (d ? { ...d, expense_method: value } : d));
  }

  expenseMethodLabel(): string {
    const method = this.currentExpenseMethod();
    if (method === 'per_unit') return 'Per Unit';
    if (method === 'pct_egai') return '% of EGAI';
    return '—';
  }

  expenseValueLabel(): string {
    return this.currentExpenseMethod() === 'per_unit' ? 'Expense per Unit' : 'Expense % of EGAI';
  }

  remarksDisplay(): string {
    return this.currentValues()?.remarks ?? '';
  }

  updateRemarks(raw: string): void {
    const value = raw.trim() === '' ? null : raw;
    this.editedDraft.update((d) => (d ? { ...d, remarks: value } : d));
  }

  metricTooltip(label: string): string | null {
    return METRIC_TOOLTIPS[label] ?? null;
  }

  metricHelpId(label: string): string {
    return 'ia-help-' + label.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  }
}

function toEditable(response: IncomeApproachResponse): EditableIncomeApproach {
  const ia = response.income_approach;
  return {
    unit_groups: response.unit_groups.map((g) => ({ ...g })),
    vacancy_rate: ia.vacancy_rate,
    other_income: ia.other_income,
    direct_assessments: ia.direct_assessments,
    direct_assessments_expense: ia.direct_assessments_expense,
    expense_method: ia.expense_method,
    expense_per_unit: ia.expense_per_unit,
    expense_pct: ia.expense_pct,
    cap_rate: ia.cap_rate,
    remarks: ia.remarks,
  };
}

function toIncomeApproachInput(editable: EditableIncomeApproach): IncomeApproachInput {
  return {
    unit_rents: editable.unit_groups.map((g) => ({
      unit_id: g.code,
      monthly_rent: g.monthly_rent,
    })),
    vacancy_rate: editable.vacancy_rate,
    other_income: editable.other_income,
    direct_assessments: editable.direct_assessments,
    direct_assessments_expense: editable.direct_assessments_expense,
    expense_method: editable.expense_method,
    expense_per_unit: editable.expense_per_unit,
    expense_pct: editable.expense_pct,
    cap_rate: editable.cap_rate,
    remarks: editable.remarks,
  };
}

function toNumberDisplay(value: number | null): string {
  return value === null ? '' : String(value);
}

function fromNumberDisplay(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isNaN(n) ? null : n;
}

function fromNumberDisplayOrZero(raw: string): number {
  return fromNumberDisplay(raw) ?? 0;
}

function toPercentDisplay(fraction: number | null): string {
  if (fraction === null) return '';
  return String(Math.round(fraction * 10000) / 100);
}

function fromPercentDisplay(raw: string): number | null {
  const n = fromNumberDisplay(raw);
  return n === null ? null : n / 100;
}

/** Build the vertical income-approach table: one metric per row, one income source per column. */
function buildTable(draft: IncomeApproachResponse): IATable {
  const groupEntries = draft.unit_groups.map((g, idx) => ({ g, idx }));
  const unitTypeEntries = groupEntries.filter((e) => !e.g.is_manager);
  const managerEntry = groupEntries.find((e) => e.g.is_manager) ?? null;

  const columns: IAColumn[] = [];
  unitTypeEntries.forEach((e) => {
    columns.push({ key: `unit-${e.idx}`, label: e.g.label, isTotal: false, groupIndex: e.idx });
  });
  if (managerEntry) {
    columns.push({
      key: 'manager',
      label: managerEntry.g.label,
      isTotal: false,
      groupIndex: managerEntry.idx,
    });
  }
  columns.push({ key: 'other-income', label: 'Other Income', isTotal: false, groupIndex: null });
  columns.push({
    key: 'direct-assessments',
    label: 'Direct Assessments',
    isTotal: false,
    groupIndex: null,
  });
  columns.push({ key: 'total', label: 'TOTAL', isTotal: true, groupIndex: null });

  const rowByLabel = (label: string): IARow | null =>
    draft.income_approach.rows.find((r) => r.label === label) ?? null;

  const perColumn = (
    pick: (row: IARow) => number,
    total: (() => number) | null,
  ): (number | null)[] => {
    const values: (number | null)[] = [];
    unitTypeEntries.forEach((e) => {
      const row = rowByLabel(e.g.label);
      values.push(row ? pick(row) : null);
    });
    if (managerEntry) {
      const row = rowByLabel('Manager Unit');
      values.push(row ? pick(row) : null);
    }
    const otherIncomeRow = rowByLabel('Other Income');
    values.push(otherIncomeRow ? pick(otherIncomeRow) : null);
    const directAssessmentsRow = rowByLabel('Direct Assessments');
    values.push(directAssessmentsRow ? pick(directAssessmentsRow) : null);
    values.push(total ? total() : null);
    return values;
  };

  const sumField = (field: keyof IARow): number =>
    draft.income_approach.rows.reduce((sum, r) => sum + (r[field] as number), 0);

  const totalUnits = unitTypeEntries.reduce((sum, e) => sum + e.g.unit_count, 0) || null;

  const unitsCells: (number | null)[] = [
    ...unitTypeEntries.map((e) => e.g.unit_count),
    ...(managerEntry ? [managerEntry.g.unit_count] : []),
    null,
    null,
    totalUnits,
  ];

  const metricRows: IAMetricRow[] = [
    { label: 'SGAI', kind: 'currency', cells: perColumn((r) => r.sgai, () => sumField('sgai')) },
    {
      label: 'V&C Losses',
      kind: 'currency',
      cells: perColumn((r) => r.vc_loss, () => sumField('vc_loss')),
    },
    { label: 'EGAI', kind: 'currency', cells: perColumn((r) => r.egai, () => sumField('egai')) },
    {
      label: 'Expenses',
      kind: 'currency',
      cells: perColumn((r) => r.expenses, () => sumField('expenses')),
    },
    { label: 'NOI', kind: 'currency', cells: perColumn((r) => r.noi, () => sumField('noi')) },
    { label: 'Overall Rate', kind: 'percent', cells: perColumn((r) => r.overall_rate, null) },
    {
      label: 'Value',
      kind: 'currency',
      cells: perColumn((r) => r.value, () => draft.income_approach.indicated_value ?? sumField('value')),
    },
  ];

  return { columns, unitsCells, metricRows };
}
